// Third-party
import React, { CSSProperties, Fragment } from 'react';
import classNames from 'classnames';
import { StickyContainer, Sticky } from 'react-sticky';
import camelCase from 'lodash/camelCase';

// Components
import ComparisonBaseCell from './ComparisonBaseCell';
import { InfoTooltip, Tooltip } from '../../../components/tooltips';
import { Checkbox, CollapseToggle, DateQuickRangeOneWay } from '../../../components/inputs';
import { SecondaryButton, PrimaryButton, IconButton } from '../../../components/buttons';
import ComparisonScroller from '../ComparisonScroller';

// Utils
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { fieldIsSelected, getAvailableFields, itemHasComments } from '@/utils/markup-tools';
import { isDuplicateField, comparisonCategoryIsShown } from '@/utils/comparison';
import { COMPARISON_CATEGORIES, MER_FIELDS, DISABLED_MER_FIELDS, TIME_RANGE_PRESET, TIME_PERIODS } from '@/utils/constants';
import dom from '@/utils/dom';
import { userHasPermissionEnabled, PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { getDateQuickRangeFromPreset } from '@/utils/time-range-presets';
import { isRiskCategory, isCalendarYearCategory } from '@/views/containers/comparison/ComparisonTable/utils/comparison-category';

// Types
import User from '@/types/user';
import Comparison from '@/types/comparison';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import SelectedCategories from '@/types/selected-categories';
import ComparisonItem from '@/types/comparison-item';
import RiskPeriods from '@/types/risk-periods';
import CalendarYearPeriod from '@/types/calendar-year-period';
import { periodToMonths } from '@/utils/time';
import Nullable from '@/types/nullable';
import OverflowGuard from '@/views/components/common/OverflowGuard';
import ComparisonCategoryHeader from '@/views/components/comparison/ComparisonCategoryHeader';

interface Props {
  user?: User | null;
  comparison: Comparison;
  categories: ComparisonCategory[];
  selectCategory: (category: ComparisonCategory, availableFields: ComparisonField[], unselectedFields?: ComparisonField[]) => void;
  toggleField: (category: ComparisonCategory, field: ComparisonField) => void;
  setExpandedFields: (fieldKey: string) => void;
  selectedCategories?: SelectedCategories;
  markupMode?: boolean;
  duplicateFields?: string[];
  viewMarkup: boolean;
  toggleViewMarkup: (enabled?: boolean) => void;
  items: ComparisonItem[];
  riskPeriod: RiskPeriods;
  calendarYearPeriod: CalendarYearPeriod;
  onRiskChange: (value: Nullable<number>) => void;
  onCalendarYearChange: (value: Nullable<number>) => void;
  scrollContainerRef?: HTMLElement;
  getAdditionalCategoryStyles: (category: ComparisonCategory) => CSSProperties;
  editedComparisonItem?: ComparisonItem;
  tablePaddingRef?: HTMLElement;
  previewMarkup: boolean;
  togglePreviewMarkup: (enabled?: boolean) => void;
  toggleCategory: (category: ComparisonCategory) => void;
  readOnly?: boolean;
  isEditPinned: boolean;
  disableStickyLabels?: boolean;
  tabSelections?: Record<string, number>;
  onTabSelectionChange?: (categoryKey: string, value: number) => void;
}

type State = {
  stickyLabels?: boolean;
};

const TOGGLE_ONLY_CATEGORIES = [
  COMPARISON_CATEGORIES.INDUSTRY_HOLDINGS,
  COMPARISON_CATEGORIES.ASSET_DETAILS,
  COMPARISON_CATEGORIES.TOP_HOLDINGS,
  COMPARISON_CATEGORIES.COUNTRY_ALLOCATIONS,
  COMPARISON_CATEGORIES.MATURITY_ALLOCATIONS,
  COMPARISON_CATEGORIES.CREDIT_QUALITY,
  COMPARISON_CATEGORIES.REGIONS,
] as string[];

class ComparisonTableLabels extends React.Component<Props, State> {
  static defaultProps = {
    disableStickyLabels: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {};
  }

  categoryIsToggleOnly = (category: ComparisonCategory) => TOGGLE_ONLY_CATEGORIES.includes(category.key);

  fieldIsDisabled = (field: ComparisonField, category: ComparisonCategory, duplicateFields?: string[]) => {
    const { readOnly, previewMarkup } = this.props;

    return (
      DISABLED_MER_FIELDS.includes(field.key) ||
      TOGGLE_ONLY_CATEGORIES.includes(category.key) ||
      (category.key === COMPARISON_CATEGORIES.KEY_METRICS && isDuplicateField(field, duplicateFields)) ||
      readOnly ||
      previewMarkup
    );
  };

  getUnselectedFields = (category: ComparisonCategory, availableFields: ComparisonField[]) => {
    const { selectedCategories } = this.props;

    if (!selectedCategories || !selectedCategories[category.key]) {
      return [];
    }

    const selectedFields = selectedCategories[category.key].fields;

    return [...availableFields].filter(field => !selectedFields.includes(field.key));
  };

  renderCategoryIcon = (unselectedFields: ComparisonField[], availableFields: ComparisonField[]) => {
    if (unselectedFields.length === availableFields.length) {
      return (
        <Checkbox
          ariaLabel={formatMessage('Common.Accessibility.SelectAllFieldsInCategory', { action: formatMessage('Common.Actions.Select') })}
          selected={false}
        />
      );
    }

    if (!unselectedFields.length) {
      return (
        <Checkbox ariaLabel={formatMessage('Common.Accessibility.SelectAllFieldsInCategory', { action: formatMessage('Common.Actions.Deselect') })} selected />
      );
    }

    return (
      <Checkbox
        ariaLabel={formatMessage('Common.Accessibility.PartiallySelectedFields', {
          selectAction: formatMessage('Common.Actions.Select').toLowerCase(),
          deselectAction: formatMessage('Common.Actions.Deselect').toLowerCase(),
        })}
        unselect
      />
    );
  };

  onRiskChange = (period: TIME_PERIODS) => {
    const months = periodToMonths(period);
    this.props.onRiskChange(months);
  };

  onCalendarYearChange = (period: TIME_PERIODS) => {
    const months = periodToMonths(period);
    this.props.onCalendarYearChange(months);
  };

  generateCategoryLabel = (category: ComparisonCategory) => {
    const { markupMode, duplicateFields, previewMarkup, comparison, readOnly, user } = this.props;

    const availableFields = getAvailableFields(category, duplicateFields);
    const unselectedFields = this.getUnselectedFields(category, availableFields);
    const isControlled = category.controlled;
    const categoryKey = category.key;
    const isRisk = isRiskCategory(categoryKey);
    const isCalendarYear = isCalendarYearCategory(categoryKey);

    const onClick = () => {
      if (!markupMode || readOnly) {
        return;
      }
      this.props.selectCategory(category, availableFields, unselectedFields);
    };

    const classes = classNames('label-container', { 'not-selectable': markupMode && readOnly });

    const riskTimePeriods = getDateQuickRangeFromPreset(
      TIME_RANGE_PRESET.RISK_DEFAULT,
      comparison.shortHistory,
      userHasPermission(user, PERMISSIONS.COMMON_INCEPTION) && comparison.items.length > 1,
    );

    return (
      <ComparisonBaseCell
        className={classNames('category-header d-flex', { controlled: isControlled, dynamic: category.dynamicConfig })}
        role="columnheader"
        id={`category-header-${categoryKey}`}
      >
        <OverflowGuard overflowText={`${category.label} ${category.info ? category.info : ''}`}>
          <span className={classes} onClick={onClick}>
            {markupMode && !previewMarkup && !readOnly && this.renderCategoryIcon(unselectedFields, availableFields)}

            <span className="label" id={`colgroup-${categoryKey}`}>
              {category.dynamicConfig ? (
                <ComparisonCategoryHeader
                  category={category}
                  onTabSelectionChange={this.props.onTabSelectionChange || (() => {})}
                  selectedValue={this.props.tabSelections?.[category.key]}
                  {...(category.info && { tooltip: <InfoTooltip title={category.label} message={category.info} place="right" /> })}
                />
              ) : (
                category.label
              )}
            </span>

            {!category.dynamicConfig && category.info && <InfoTooltip title={category.label} message={category.info} place="right" />}

            {!markupMode && category.toggleable && (
              <div className="collapse-toggle-container">
                <CollapseToggle checked={comparison.toggledCategories.includes(categoryKey)} onChange={() => this.props.toggleCategory(category)} />
              </div>
            )}
          </span>
        </OverflowGuard>

        {isControlled && isRisk && (
          <div id={`date-range-risk-${categoryKey}`}>
            <DateQuickRangeOneWay
              selected={this.props.riskPeriod as TIME_PERIODS}
              onChange={this.onRiskChange}
              /* readOnly={readOnly} */ values={riskTimePeriods}
            />
          </div>
        )}

        {isControlled && isCalendarYear && (
          <div id={`date-range-calendar-${categoryKey}`}>
            <DateQuickRangeOneWay
              values={getDateQuickRangeFromPreset(TIME_RANGE_PRESET.CALENDAR_YEAR_DEFAULT)}
              selected={this.props.calendarYearPeriod as TIME_PERIODS}
              onChange={this.onCalendarYearChange}
              /* readOnly={readOnly} */
            />
          </div>
        )}
      </ComparisonBaseCell>
    );
  };

  generateCategoryField = (field: ComparisonField, i: number, category: ComparisonCategory) => {
    const { comparison, markupMode, selectedCategories, duplicateFields, previewMarkup, readOnly } = this.props;
    const fieldIsDisabled = this.fieldIsDisabled(field, category, duplicateFields);
    const fieldIsExpandable = field.subFields?.length;
    const isFieldExpanded = comparison.expandedFields?.includes(field.key);

    const onClick = () => {
      if (!markupMode || fieldIsDisabled || !selectedCategories || readOnly) {
        return;
      }

      this.props.toggleField(category, field);

      if (field.key === MER_FIELDS.MER) {
        const merFields = category.fields.filter(f => DISABLED_MER_FIELDS.includes(f.key));
        for (const merField of merFields) {
          this.props.toggleField(category, merField);
        }
      }
    };

    const onToggleExpand = (e: React.MouseEvent) => {
      e.stopPropagation();
      this.props.setExpandedFields(field.key);
    };

    const classes = classNames('label-container', { 'not-selectable': markupMode && fieldIsDisabled });
    const expandableFieldClassName = classNames(`toggle-field-expand ${field.key}`, { expanded: isFieldExpanded });

    return (
      <Fragment key={i}>
        <ComparisonBaseCell className={field.key} role="columnheader" id={`field-header-${field.key}`}>
          <OverflowGuard overflowText={`${category.label}: ${field.label} ${field.info ? field.info : ''}`}>
            <div className={classes} onClick={onClick} aria-label={`${category.label} ${field.label}`}>
              {markupMode && selectedCategories && !previewMarkup && (
                <Checkbox
                  ariaLabel={formatMessage('Common.Accessibility.SelectField', {
                    action: formatMessage(`Common.Actions.${fieldIsSelected(field, category, selectedCategories) ? 'Deselect' : 'Select'}`),
                    fieldLabel: field.label || '',
                  })}
                  selected={fieldIsSelected(field, category, selectedCategories)}
                  disabled={fieldIsDisabled}
                />
              )}

              {fieldIsExpandable && <IconButton className={expandableFieldClassName} icon="ds-arrow-right" onClick={onToggleExpand} />}

              <span aria-hidden="true">{!field.labelComponent ? field.label : React.createElement(field.labelComponent, { ...field })}</span>

              {field.info && <InfoTooltip place="right" title={field.label} message={field.info} />}
            </div>
          </OverflowGuard>
        </ComparisonBaseCell>
        {fieldIsExpandable &&
          isFieldExpanded &&
          field.subFields?.map((subField, j) => (
            <ComparisonBaseCell key={`${field.key}-sub-${j}`} className={`${field.key}-subfield subfield-item`}>
              <span className={`${classes} subfield-label`} onClick={onClick}>
                {markupMode && selectedCategories && !previewMarkup && (
                  <Checkbox
                    ariaLabel={formatMessage('Common.Accessibility.SelectSubField', {
                      action: formatMessage(`Common.Actions.${fieldIsSelected(subField, category, selectedCategories) ? 'Deselect' : 'Select'}`),
                      fieldLabel: subField.label || '',
                    })}
                    selected={fieldIsSelected(subField, category, selectedCategories)}
                    disabled={fieldIsDisabled}
                  />
                )}
                {!subField.labelComponent ? subField.label : React.createElement(subField.labelComponent, { ...subField })}
                {subField.info && <InfoTooltip place="right" title={subField.label} message={subField.info} />}
              </span>
            </ComparisonBaseCell>
          ))}
      </Fragment>
    );
  };

  previewMarkupDisabled = () => {
    const { previewMarkup, categories, selectedCategories, duplicateFields, items } = this.props;

    let hasUnselectedField = false;

    if (!selectedCategories) {
      return true;
    }

    for (const category of categories) {
      const availableFields = getAvailableFields(category, duplicateFields);

      if (selectedCategories[category.key] && selectedCategories[category.key].fields.length !== availableFields.length) {
        hasUnselectedField = true;
        break;
      }
    }

    const hasItemsWithComments = items.some(item => itemHasComments(item, selectedCategories));

    return !previewMarkup && !hasUnselectedField && !hasItemsWithComments;
  };

  togglePreviewMarkup = () => {
    this.props.togglePreviewMarkup();

    // Refresh sticky headers
    setTimeout(() => {
      dom.fireResizeEvent();
    }, 100);
  };

  toggleViewMarkup = () => {
    this.props.toggleViewMarkup();

    // Refresh sticky headers
    setTimeout(() => {
      dom.fireResizeEvent();
    }, 100);
  };

  renderMarkupButtons = () => {
    const { viewMarkup, items, user, markupMode, previewMarkup } = this.props;
    const { stickyLabels } = this.state;

    const tooltipPosition = stickyLabels ? 'bottom' : undefined;

    const hasMarkup = items.some(i => !!i.markup);
    const hasMarkupPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP);
    let el;

    if (hasMarkupPermission && markupMode) {
      const MarkupButtonAM = previewMarkup ? PrimaryButton : SecondaryButton;
      const previewDisabled = this.previewMarkupDisabled();
      const missingMarkup = [
        formatMessage('MarkupTools.PreviewDisabled.MissingComment'),
        formatMessage('MarkupTools.PreviewDisabled.MissingSelectedCategories'),
      ];

      el = (
        <Tooltip place={tooltipPosition} message={formatMessage('MarkupTools.PreviewDisabled.Title')} list={missingMarkup} disabled={!previewDisabled}>
          <span>
            <MarkupButtonAM className="toggle-markup-button" compact onClick={this.togglePreviewMarkup} disabled={previewDisabled}>
              <i className={`icon-${!previewMarkup ? 'eye' : 'eye-with-cross'}`} />
              <FormattedMessage id={`MarkupTools.${!previewMarkup ? 'Preview' : 'ExitPreview'}`} />
            </MarkupButtonAM>
          </span>
        </Tooltip>
      );
    }

    if (!hasMarkupPermission && hasMarkup) {
      const MarkupButtonFA = viewMarkup ? PrimaryButton : SecondaryButton;

      el = (
        <MarkupButtonFA className="toggle-markup-button" compact onClick={this.toggleViewMarkup}>
          <i className="icon-highlighter" />
          <FormattedMessage id={`MarkupTools.${viewMarkup ? 'HideMarkup' : 'ViewMarkup'}`} />
        </MarkupButtonFA>
      );
    }

    return el && <div className="toggle-markup-container">{el}</div>;
  };

  getKeyMetricsHeight = (category: ComparisonCategory) => {
    if (category.key !== COMPARISON_CATEGORIES.KEY_METRICS) {
      return undefined;
    }

    return this.props.getAdditionalCategoryStyles(category);
  };

  updateStickyLabels = (isSticky: boolean) => {
    this.setState({ stickyLabels: isSticky });
  };

  render() {
    const {
      categories,
      items,
      scrollContainerRef,
      tablePaddingRef,
      comparison,
      markupMode,
      editedComparisonItem,
      viewMarkup,
      previewMarkup,
      isEditPinned,
      disableStickyLabels,
    } = this.props;

    const { stickyLabels } = this.state;

    return (
      <StickyContainer className={classNames('comparison-table-labels', { 'sticky-labels': stickyLabels && !disableStickyLabels })} role="rowgroup">
        <Sticky disableCompensation={disableStickyLabels} topOffset={-20}>
          {({ style, isSticky }) => {
            const sticky = isSticky && !disableStickyLabels;

            const classes = classNames('comparison-table-labels-actions d-flex flex-column justify-content-center align-items-start', {
              'is-sticky': sticky,
            });

            if (stickyLabels !== sticky) {
              this.updateStickyLabels(sticky);
            }

            return (
              <div style={sticky ? style : undefined} className={classes}>
                {this.renderMarkupButtons()}

                {!sticky && (
                  <ComparisonScroller
                    items={items}
                    scrollContainerRef={scrollContainerRef}
                    markupMode={markupMode}
                    editedComparisonItem={editedComparisonItem}
                    viewMarkup={viewMarkup}
                    previewMarkup={previewMarkup}
                    tablePaddingRef={tablePaddingRef}
                    isEditPinned={isEditPinned}
                  />
                )}
              </div>
            );
          }}
        </Sticky>

        <div className="comparison-label-categories" role="row">
          {categories.map(category => {
            const showFields = comparisonCategoryIsShown(comparison, category);

            return (
              <div className={`comparison-category ${category.key}`} style={this.getKeyMetricsHeight(category)} key={category.key} role="columngroup">
                {this.generateCategoryLabel(category)}

                {showFields && category.fields.map((field, i) => this.generateCategoryField(field, i, category))}
              </div>
            );
          })}
        </div>
      </StickyContainer>
    );
  }
}

export default ComparisonTableLabels;
